import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeConnectService } from '../../../services/stripeConnectService';
import receiptService from '../../../services/receiptService';

const TaxDocumentation = () => {
  const [selectedYear, setSelectedYear] = useState('2026');
  const [w9Status, setW9Status] = useState(null);
  const [isLoadingW9, setIsLoadingW9] = useState(true);
  const [taxDocuments, setTaxDocuments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const quarterlyEarnings = [];

  const expenseCategories = [];

  useEffect(() => {
    loadW9Status();
  }, []);

  const loadW9Status = async () => {
    try {
      setIsLoadingW9(true);
      const status = await stripeConnectService?.getW9Status();
      setW9Status(status);
    } catch (err) {
      console.error('Failed to load W-9 status:', err);
    } finally {
      setIsLoadingW9(false);
    }
  };

  const handleDownload = (documentId) => {
    alert(`Downloading tax document ${documentId}...`);
  };

  const handleGenerate1099 = async () => {
    try {
      setIsGenerating(true);
      setGenerationError(null);
      
      const result = await receiptService?.generate1099NEC(selectedYear);
      
      if (result?.success) {
        alert(`1099-NEC form generated successfully!\n\nTotal Earnings: $${result?.totalEarnings?.toFixed(2)}\nPayouts: ${result?.payoutCount}\nFile: ${result?.fileName}`);
      }
    } catch (err) {
      console.error('Failed to generate 1099-NEC:', err);
      setGenerationError(err?.message || 'Failed to generate 1099-NEC form');
    } finally {
      setIsGenerating(false);
    }
  };

  const getW9StatusColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'submitted': return 'warning';
      case 'pending': return 'error';
      default: return 'muted-foreground';
    }
  };

  const getW9StatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'submitted': return 'Clock';
      case 'pending': return 'AlertCircle';
      default: return 'FileText';
    }
  };

  return (
    <div className="space-y-6">
      {/* W-9 Status Card */}
      {!isLoadingW9 && w9Status && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
              <Icon name="FileCheck" size={24} strokeWidth={2} className="text-primary" />
              <span>W-9 Tax Information</span>
            </h2>
            <span className={`px-3 py-1 bg-${getW9StatusColor(w9Status?.w9SubmissionStatus)}/10 text-${getW9StatusColor(w9Status?.w9SubmissionStatus)} text-sm font-medium rounded-full flex items-center space-x-1`}>
              <Icon name={getW9StatusIcon(w9Status?.w9SubmissionStatus)} size={16} strokeWidth={2} />
              <span className="capitalize">{w9Status?.w9SubmissionStatus || 'Unknown'}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Submission Status */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Upload" size={20} className="text-primary" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-foreground mb-1">Submission Status</h3>
                  <p className="font-caption text-xs text-muted-foreground">
                    {w9Status?.w9SubmissionDate 
                      ? `Submitted on ${new Date(w9Status?.w9SubmissionDate)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Not yet submitted'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={w9Status?.w9SubmissionStatus === 'verified' ? 'CheckCircle' : w9Status?.w9SubmissionStatus === 'submitted' ? 'Clock' : 'AlertCircle'} 
                  size={16} 
                  className={`text-${getW9StatusColor(w9Status?.w9SubmissionStatus)}`} 
                  strokeWidth={2} 
                />
                <span className="font-body text-sm text-foreground capitalize">
                  {w9Status?.w9SubmissionStatus === 'verified' ? 'Verified and Complete' : w9Status?.w9SubmissionStatus === 'submitted' ? 'Under Review' : 'Action Required'}
                </span>
              </div>
            </div>

            {/* Verification Status */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldCheck" size={20} className="text-success" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-foreground mb-1">Verification Status</h3>
                  <p className="font-caption text-xs text-muted-foreground">
                    {w9Status?.w9VerifiedDate 
                      ? `Verified on ${new Date(w9Status?.w9VerifiedDate)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Pending verification'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={w9Status?.w9VerificationCompleted ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={w9Status?.w9VerificationCompleted ? 'text-success' : 'text-error'} 
                  strokeWidth={2} 
                />
                <span className="font-body text-sm text-foreground">
                  {w9Status?.w9VerificationCompleted ? 'Verification Complete' : 'Not Verified'}
                </span>
              </div>
            </div>

            {/* 1099-K Eligibility */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={20} className="text-accent" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-foreground mb-1">1099-K Eligibility</h3>
                  <p className="font-caption text-xs text-muted-foreground">
                    IRS Form 1099-K reporting status
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={w9Status?.is1099kEligible ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={w9Status?.is1099kEligible ? 'text-success' : 'text-muted-foreground'} 
                  strokeWidth={2} 
                />
                <span className="font-body text-sm text-foreground">
                  {w9Status?.is1099kEligible ? 'Eligible for 1099-K' : 'Not Yet Eligible'}
                </span>
              </div>
            </div>

            {/* Requirements Status */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} className="text-warning" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-foreground mb-1">Outstanding Requirements</h3>
                  <p className="font-caption text-xs text-muted-foreground">
                    Items needed for compliance
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                {w9Status?.requirementsCurrentlyDue?.length > 0 ? (
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error" strokeWidth={2} />
                    <span className="font-body text-sm text-error">
                      {w9Status?.requirementsCurrentlyDue?.length} item(s) due now
                    </span>
                  </div>
                ) : w9Status?.requirementsPastDue?.length > 0 ? (
                  <div className="flex items-center space-x-2">
                    <Icon name="XCircle" size={16} className="text-error" strokeWidth={2} />
                    <span className="font-body text-sm text-error">
                      {w9Status?.requirementsPastDue?.length} item(s) overdue
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success" strokeWidth={2} />
                    <span className="font-body text-sm text-success">
                      All requirements met
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          {!w9Status?.w9VerificationCompleted && (
            <div className="mt-6 p-4 bg-warning/5 border border-warning/20 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <p className="font-body text-sm text-foreground mb-2">
                    Complete your W-9 tax information to enable payouts and 1099-K reporting.
                  </p>
                  <Button variant="default" size="sm" iconName="ExternalLink">
                    Complete W-9 Setup
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tax Documents */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="FileText" size={24} strokeWidth={2} className="text-primary" />
            <span>Tax Documents</span>
          </h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
          </select>
        </div>

        {taxDocuments?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-muted-foreground">No tax documents available for {selectedYear}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {taxDocuments?.map((doc) => (
              <div key={doc?.id} className="border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-smooth">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={24} className="text-primary" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-body font-semibold text-foreground">Form {doc?.type}</h3>
                        <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full flex items-center space-x-1">
                          <Icon name="CheckCircle" size={12} strokeWidth={2} />
                          <span>Available</span>
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="font-body text-sm text-muted-foreground">Tax Year: {doc?.year}</p>
                        <p className="font-body text-sm text-muted-foreground">
                          Total Earnings: <span className="font-bold text-foreground">${doc?.totalEarnings?.toLocaleString()}</span>
                        </p>
                        <p className="font-caption text-xs text-muted-foreground">
                          Generated: {new Date(doc?.dateGenerated)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="default" size="sm" iconName="Download" onClick={() => handleDownload(doc?.id)}>
                      Download PDF
                    </Button>
                    <Button variant="outline" size="sm" iconName="Eye">
                      Preview
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="font-body text-sm text-muted-foreground">
                Form 1099-K is issued when you earn $600 or more in a calendar year. Documents are typically available by January 31st.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quarterly Earnings Summary */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="BarChart3" size={20} strokeWidth={2} className="text-success" />
          <span>Quarterly Earnings Summary</span>
        </h2>

        <div className="space-y-3">
          {quarterlyEarnings?.map((quarter, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-body font-semibold text-foreground mb-2">{quarter?.quarter}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="font-caption text-xs text-muted-foreground mb-1">Gross Earnings</p>
                      <p className="font-body text-lg font-bold text-foreground">${quarter?.earnings?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="font-caption text-xs text-muted-foreground mb-1">Bookings</p>
                      <p className="font-body text-lg font-bold text-foreground">{quarter?.bookings}</p>
                    </div>
                    <div>
                      <p className="font-caption text-xs text-muted-foreground mb-1">Expenses</p>
                      <p className="font-body text-lg font-bold text-error">${quarter?.expenses?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" iconName="Download">
                  Export
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Expense Tracking */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Receipt" size={20} strokeWidth={2} className="text-warning" />
            <span>Expense Breakdown</span>
          </h2>
          <Button variant="outline" size="sm" iconName="Plus">
            Add Expense
          </Button>
        </div>

        <div className="space-y-4">
          {expenseCategories?.map((expense, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={16} className="text-muted-foreground" strokeWidth={2} />
                  <span className="font-body text-sm text-foreground">{expense?.category}</span>
                </div>
                <span className="font-body text-sm font-bold text-foreground">${expense?.amount?.toLocaleString()}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-warning h-2 rounded-full transition-all duration-500"
                  style={{ width: `${expense?.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="font-body font-semibold text-foreground">Total Expenses (2026)</span>
            <span className="font-heading text-2xl font-bold text-error">
              ${expenseCategories?.reduce((sum, exp) => sum + exp?.amount, 0)?.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Tax Preparation Tips */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Lightbulb" size={20} strokeWidth={2} className="text-accent" />
          <span>Tax Preparation Tips</span>
        </h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-1" strokeWidth={2} />
            <p className="font-body text-sm text-muted-foreground">
              Keep detailed records of all trailer-related expenses including maintenance, insurance, and registration fees
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-1" strokeWidth={2} />
            <p className="font-body text-sm text-muted-foreground">
              Track mileage for trailer pickups, deliveries, and maintenance trips - these may be deductible
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-1" strokeWidth={2} />
            <p className="font-body text-sm text-muted-foreground">
              Consider quarterly estimated tax payments if you expect to owe $1,000 or more in taxes
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-1" strokeWidth={2} />
            <p className="font-body text-sm text-muted-foreground">
              Consult with a tax professional to maximize deductions and ensure compliance with tax laws
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <h3 className="font-body font-semibold text-foreground mb-2">Tax Disclaimer</h3>
            <p className="font-body text-sm text-muted-foreground">
              The information provided here is for reference only and should not be considered tax advice. Please consult with a qualified tax professional for guidance specific to your situation.
            </p>
          </div>
        </div>
      </div>

      {/* 1099-NEC Generation Card */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="FileText" size={24} strokeWidth={2} className="text-primary" />
            <span>1099-NEC Tax Form</span>
          </h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
          </select>
        </div>

        {generationError && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3 mb-6">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="font-body text-sm text-error">{generationError}</p>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={32} className="text-accent" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Generate {selectedYear} 1099-NEC Form
              </h3>
              <p className="font-body text-sm text-muted-foreground mb-4">
                Download your annual nonemployee compensation form for tax filing. This form includes all completed payouts and platform fee deductions for the selected tax year.
              </p>
              
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="font-body text-xs text-foreground mb-2">
                      <strong>What's included in your 1099-NEC:</strong>
                    </p>
                    <ul className="font-caption text-xs text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>Total nonemployee compensation (net earnings after platform fees)</li>
                      <li>Annual earnings breakdown with gross amounts and fee deductions</li>
                      <li>Platform and recipient information for IRS reporting</li>
                      <li>Complete payout transaction summary for {selectedYear}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Download"
                  onClick={handleGenerate1099}
                  disabled={isGenerating || !w9Status?.w9VerificationCompleted}
                >
                  {isGenerating ? 'Generating...' : `Download ${selectedYear} 1099-NEC`}
                </Button>
                
                {!w9Status?.w9VerificationCompleted && (
                  <span className="font-caption text-xs text-warning flex items-center space-x-1">
                    <Icon name="AlertTriangle" size={14} strokeWidth={2} />
                    <span>W-9 verification required</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tax Filing Guidance */}
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="BookOpen" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <h4 className="font-body font-semibold text-foreground mb-2">Tax Filing Guidance</h4>
              <ul className="font-caption text-sm text-muted-foreground space-y-2">
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span>Report the amount from Box 1 on Schedule C (Form 1040) or Schedule C-EZ</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span>Platform fees are already deducted and reflected in your net compensation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span>You may be able to deduct business expenses related to your trailer rental activity</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span>Consult with a qualified tax professional for personalized tax advice</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxDocumentation;